import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  User, DollarSign, Key, Bell, Shield, ChevronRight,
  CreditCard, Users, Settings, Building2, LogOut, CheckCircle, XCircle,
} from "lucide-react";
import MfaSettings from "@/components/app/MfaSettings";

// ── Sidebar nav item ──────────────────────────────────────────────────────────

const NavItem = ({ icon: Icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors text-left ${
      active
        ? "bg-violet-50 text-violet-700"
        : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
    }`}
  >
    <Icon className={`w-4 h-4 shrink-0 ${active ? "text-violet-600" : "text-gray-400"}`} strokeWidth={1.75} />
    {label}
  </button>
);

// ── Card wrapper ──────────────────────────────────────────────────────────────

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
    {(title || subtitle) && (
      <div className="px-6 py-5 border-b border-gray-50">
        {title && <h2 className="text-base font-semibold text-gray-800">{title}</h2>}
        {subtitle && <p className="text-sm text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
    )}
    <div className="px-6 py-5">{children}</div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-xs font-semibold text-gray-600 mb-1.5 block">{label}</Label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const inputCls = "rounded-xl border-gray-200 bg-gray-50 text-gray-800 h-10 focus:border-violet-400 focus:ring-1 focus:ring-violet-200 transition-colors";

// ── Toggle ────────────────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative w-10 h-6 rounded-full transition-colors ${checked ? "bg-violet-600" : "bg-gray-200"}`}
  >
    <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0"}`} />
  </button>
);

// ── Save button ───────────────────────────────────────────────────────────────

const SaveBtn = ({ onClick, loading, label = "Save changes" }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className="px-5 py-2 bg-violet-600 text-white text-sm font-semibold rounded-full hover:bg-violet-700 transition-colors disabled:opacity-50 shadow-sm shadow-violet-200"
  >
    {loading ? "Saving…" : label}
  </button>
);

// ── TABS ──────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "profile",       icon: User,       label: "Profile settings" },
  { id: "billing",       icon: CreditCard, label: "Plan & billing" },
  { id: "rates",         icon: DollarSign, label: "Billing rates" },
  { id: "password",      icon: Key,        label: "Password & security" },
  { id: "notifications", icon: Bell,       label: "Notifications" },
  { id: "session",       icon: Shield,     label: "Session info" },
];

// ── MAIN ──────────────────────────────────────────────────────────────────────

const Account = () => {
  const { user: authUser, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("profile");
  const [profile, setProfile] = useState(null);

  const [form, setForm] = useState({ name: "", timezone: "" });
  const [rates, setRates] = useState({ default_hourly_rate: "", hourly_cost_rate: "", weekly_capacity_hours: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [notifPrefs, setNotifPrefs] = useState({
    task_assigned: true, task_overdue: true,
    timesheet_approved: true, timesheet_rejected: true,
    invoice_paid: true, raid_opened: true, compliance_due: true,
  });

  const [saving, setSaving] = useState(false);
  const [ratesSaving, setRatesSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      setProfile(data);
      setForm({ name: data.name || "", timezone: data.timezone || "" });
      setRates({
        default_hourly_rate: data.default_hourly_rate ?? "",
        hourly_cost_rate: data.hourly_cost_rate ?? "",
        weekly_capacity_hours: data.weekly_capacity_hours ?? "",
      });
      if (data.notification_prefs) setNotifPrefs(p => ({ ...p, ...data.notification_prefs }));
    }).catch(() => toast.error("Failed to load profile"));
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const body = {};
      if (form.name.trim()) body.name = form.name.trim();
      if (form.timezone.trim()) body.timezone = form.timezone.trim();
      const { data } = await api.patch("/auth/me", body);
      setProfile(data);
      toast.success("Profile updated");
      if (refreshUser) refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveRates = async () => {
    setRatesSaving(true);
    try {
      const body = {};
      if (rates.default_hourly_rate !== "") body.default_hourly_rate = Number(rates.default_hourly_rate);
      if (rates.hourly_cost_rate !== "") body.hourly_cost_rate = Number(rates.hourly_cost_rate);
      if (rates.weekly_capacity_hours !== "") body.weekly_capacity_hours = Number(rates.weekly_capacity_hours);
      const { data } = await api.patch("/auth/me", body);
      setProfile(data);
      toast.success("Rates updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setRatesSaving(false); }
  };

  const changePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) return toast.error("Fill all fields");
    if (pwForm.new_password !== pwForm.confirm_password) return toast.error("Passwords do not match");
    if (pwForm.new_password.length < 8) return toast.error("Password must be at least 8 characters");
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Password changed");
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Password change failed"); }
    finally { setPwSaving(false); }
  };

  const saveNotifPrefs = async () => {
    setNotifSaving(true);
    try {
      await api.patch("/auth/me", { notification_prefs: notifPrefs });
      toast.success("Notification preferences saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setNotifSaving(false); }
  };

  const ROLE_COLOR = {
    owner: "bg-amber-50 text-amber-700 border border-amber-200",
    admin: "bg-violet-50 text-violet-700 border border-violet-200",
    member: "bg-gray-100 text-gray-600",
    viewer: "bg-gray-100 text-gray-500",
  };

  const initials = (profile?.name || profile?.email || "?").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-5">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-violet-600 flex items-center justify-center text-white font-bold text-lg shadow-md shadow-violet-200">
            {profile?.picture
              ? <img src={profile.picture} alt={profile.name} className="w-full h-full object-cover rounded-2xl" />
              : initials}
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{profile?.name || profile?.email || "Account"}</h1>
            <p className="text-sm text-gray-400">{profile?.email}</p>
          </div>
          {profile?.role && (
            <span className={`ml-auto text-xs font-semibold px-3 py-1 rounded-full capitalize ${ROLE_COLOR[profile.role] || ROLE_COLOR.member}`}>
              {profile.role}
            </span>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 flex gap-8">
        {/* Left sidebar nav */}
        <aside className="w-56 shrink-0 space-y-1">
          {/* My Account group */}
          <p className="px-4 text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-2">My Account</p>
          {TABS.slice(0, 2).map(t => (
            <NavItem key={t.id} icon={t.icon} label={t.label} active={tab === t.id} onClick={() => setTab(t.id)} />
          ))}

          <div className="border-t border-gray-100 my-3" />

          <p className="px-4 text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-2">Options</p>
          {TABS.slice(2).map(t => (
            <NavItem key={t.id} icon={t.icon} label={t.label} active={tab === t.id} onClick={() => setTab(t.id)} />
          ))}

          <div className="border-t border-gray-100 my-3" />

          <button
            onClick={() => navigate("/team")}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-800 transition-colors"
          >
            <Users className="w-4 h-4 text-gray-400" strokeWidth={1.75} />
            Manage users
          </button>
          <button
            onClick={() => navigate("/company")}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-800 transition-colors"
          >
            <Building2 className="w-4 h-4 text-gray-400" strokeWidth={1.75} />
            Company &amp; logo
          </button>
          <button
            onClick={async () => { try { await api.post("/auth/logout"); } catch {} window.location.href = "/"; }}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
            Log out
          </button>
        </aside>

        {/* Right content */}
        <main className="flex-1 space-y-5">

          {/* ── Profile ── */}
          {tab === "profile" && (
            <>
              <Card title="Profile information" subtitle="Update your display name and timezone.">
                <div className="space-y-4">
                  <Field label="Display name">
                    <Input className={inputCls} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="Your full name" data-testid="account-name-input" />
                  </Field>
                  <Field label="Email address" hint="Contact support to change your email.">
                    <Input className={inputCls} value={profile?.email || ""} disabled />
                  </Field>
                  <Field label="Timezone" hint="Used for due date calculations and notifications.">
                    <Input className={inputCls} value={form.timezone} onChange={e => setForm({ ...form, timezone: e.target.value })}
                      placeholder="e.g. Europe/London" />
                  </Field>
                  <div className="pt-2 flex justify-end">
                    <SaveBtn onClick={saveProfile} loading={saving} />
                  </div>
                </div>
              </Card>
            </>
          )}

          {/* ── Plan & billing ── */}
          {tab === "billing" && (
            <Card title="Plan & billing" subtitle="Your current plan and billing details.">
              <div className="flex items-center justify-between py-3 border border-gray-100 rounded-xl px-4 mb-4">
                <div>
                  <div className="text-sm font-semibold text-gray-800 capitalize">{(profile?.plan || "free").replace(/_/g, " ")} plan</div>
                  <div className="text-xs text-gray-400 mt-0.5">Renews monthly</div>
                </div>
                <button onClick={() => navigate("/billing")}
                  className="px-4 py-1.5 bg-violet-600 text-white text-xs font-semibold rounded-full hover:bg-violet-700 transition-colors">
                  Upgrade
                </button>
              </div>
              <button onClick={() => navigate("/billing")}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-sm text-gray-700">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  Manage billing &amp; invoices
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </button>
            </Card>
          )}

          {/* ── Billing rates ── */}
          {tab === "rates" && (
            <Card title="Billing rates &amp; capacity" subtitle="Used on invoices, margin calculations, and the resource grid.">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Billing rate (per hr)">
                    <Input type="number" min="0" step="5" className={inputCls}
                      value={rates.default_hourly_rate} onChange={e => setRates({ ...rates, default_hourly_rate: e.target.value })}
                      placeholder="e.g. 150" data-testid="account-rate-input" />
                  </Field>
                  <Field label="Cost rate (per hr)">
                    <Input type="number" min="0" step="5" className={inputCls}
                      value={rates.hourly_cost_rate} onChange={e => setRates({ ...rates, hourly_cost_rate: e.target.value })}
                      placeholder="e.g. 80" />
                  </Field>
                </div>
                <Field label="Weekly capacity (hours)" hint="Affects resource utilisation calculations.">
                  <Input type="number" min="0" max="168" className={inputCls}
                    value={rates.weekly_capacity_hours} onChange={e => setRates({ ...rates, weekly_capacity_hours: e.target.value })}
                    placeholder="Default 40" data-testid="account-capacity-input" />
                </Field>
                <div className="pt-2 flex justify-end">
                  <SaveBtn onClick={saveRates} loading={ratesSaving} />
                </div>
              </div>
            </Card>
          )}

          {/* ── Password ── */}
          {tab === "password" && (
            <Card title="Password &amp; security" subtitle="Change your password. Minimum 8 characters.">
              <div className="space-y-4">
                <Field label="Current password">
                  <Input type="password" className={inputCls} value={pwForm.current_password}
                    onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })}
                    data-testid="account-current-pw" />
                </Field>
                <Field label="New password">
                  <Input type="password" className={inputCls} value={pwForm.new_password}
                    onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })}
                    data-testid="account-new-pw" />
                </Field>
                <Field label="Confirm new password">
                  <Input type="password" className={inputCls} value={pwForm.confirm_password}
                    onChange={e => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                    data-testid="account-confirm-pw" />
                </Field>
                <div className="pt-2 flex justify-end">
                  <SaveBtn onClick={changePassword} loading={pwSaving} label="Update password" data-testid="change-pw-btn" />
                </div>
              </div>
            </Card>
          )}

          {/* ── Two-factor authentication ── */}
          {tab === "password" && (
            <Card title="Two-factor authentication" subtitle="Add an authenticator app as a second factor when you sign in.">
              <MfaSettings />
            </Card>
          )}

          {/* ── Notifications ── */}
          {tab === "notifications" && (
            <Card title="Notification preferences" subtitle="Choose which events send you a notification.">
              <div className="space-y-1">
                {[
                  { key: "task_assigned",      label: "Task assigned to me",      desc: "When a task is assigned to you" },
                  { key: "task_overdue",        label: "Task overdue alerts",      desc: "When a task you own passes its due date" },
                  { key: "timesheet_approved",  label: "Timesheet approved",       desc: "When a manager approves your timesheet" },
                  { key: "timesheet_rejected",  label: "Timesheet rejected",       desc: "When a timesheet is sent back for revision" },
                  { key: "invoice_paid",        label: "Invoice marked paid",      desc: "When a client invoice is marked as paid" },
                  { key: "raid_opened",         label: "New RAID item raised",     desc: "When a new risk, issue or dependency is logged" },
                  { key: "compliance_due",      label: "Compliance item due soon", desc: "Reminder when a compliance deadline approaches" },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                    <div>
                      <div className="text-sm font-medium text-gray-700">{label}</div>
                      <div className="text-xs text-gray-400">{desc}</div>
                    </div>
                    <Toggle checked={notifPrefs[key]} onChange={v => setNotifPrefs(p => ({ ...p, [key]: v }))} />
                  </div>
                ))}
              </div>
              <div className="pt-4 flex justify-end">
                <SaveBtn onClick={saveNotifPrefs} loading={notifSaving} label="Save preferences" data-testid="save-notif-prefs-btn" />
              </div>
            </Card>
          )}

          {/* ── Session ── */}
          {tab === "session" && (
            <Card title="Session &amp; account info" subtitle="Read-only identifiers for your account.">
              <div className="space-y-3">
                {[
                  { label: "Tenant ID", value: profile?.tenant_id },
                  { label: "User ID", value: profile?.user_id },
                  { label: "Role", value: profile?.role },
                  { label: "Plan", value: profile?.plan },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <span className="text-sm text-gray-500">{label}</span>
                    <span className="text-xs font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded-lg">{value || "—"}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-500">Email verified</span>
                  <div className="flex items-center gap-1.5">
                    {profile?.email_verified
                      ? <><CheckCircle className="w-4 h-4 text-green-500" /><span className="text-xs text-green-600 font-medium">Verified</span></>
                      : <><XCircle className="w-4 h-4 text-red-400" /><span className="text-xs text-red-500 font-medium">Not verified</span></>
                    }
                  </div>
                </div>
              </div>
            </Card>
          )}

        </main>
      </div>
    </div>
  );
};

export default Account;
